import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { InstagramIcon, FacebookIcon } from "@/components/Mask";
import { CONTACT, STORES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", email: "", phone: "", message: "" };

const INFO = [
  { icon: MapPin, label: "Outlets", value: "2 stores open · Sarjapur & Begur (1 more coming soon)" },
  { icon: Phone, label: "Phone", value: CONTACT.phone, href: `tel:${CONTACT.phoneLink}` },
  { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
  { icon: Clock, label: "Opening Hours", value: `Daily · ${CONTACT.hours}` },
];

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact-messages`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-28">
      <section className="mx-auto max-w-7xl px-5 py-16 md:py-20">
        <Reveal><p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">Contact Us</p></Reveal>
        <Reveal delay={0.05}>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl md:text-7xl font-bold uppercase tracking-tight text-koreyaah-indigo leading-[0.9]">
            Come say <span className="text-koreyaah-red">annyeong</span>
          </h1>
        </Reveal>
        <Reveal delay={0.1}><p className="mt-6 max-w-2xl text-lg text-koreyaah-indigo/80">Questions, feedback, or just craving ramen? Reach out — we'd love to hear from you.</p></Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 grid gap-10 lg:grid-cols-2">
        {/* Info + socials */}
        <div>
          <div className="grid gap-5 sm:grid-cols-2">
            {INFO.map((it) => {
              const Inner = (
                <>
                  <div className="grid h-12 w-12 place-items-center rounded-2xl bg-koreyaah-red text-white"><it.icon size={22} /></div>
                  <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-koreyaah-red">{it.label}</p>
                  <p className="mt-2 text-koreyaah-indigo/80 leading-relaxed">{it.value}</p>
                </>
              );
              return (
                <Reveal key={it.label}>
                  {it.href ? (
                    <a href={it.href} target="_blank" rel="noreferrer" className="block h-full rounded-[2rem] border-2 border-koreyaah-indigo/10 bg-white p-6 transition-transform duration-300 hover:-translate-y-1">{Inner}</a>
                  ) : (
                    <div className="h-full rounded-[2rem] border-2 border-koreyaah-indigo/10 bg-white p-6">{Inner}</div>
                  )}
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-6 flex items-center gap-3">
              <a href={CONTACT.social.instagram} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="grid h-12 w-12 place-items-center rounded-full bg-koreyaah-indigo text-white transition-colors duration-200 hover:bg-koreyaah-red"><InstagramIcon size={20} /></a>
              <a href={CONTACT.social.facebook} target="_blank" rel="noreferrer" data-testid="contact-facebook" className="grid h-12 w-12 place-items-center rounded-full bg-koreyaah-indigo text-white transition-colors duration-200 hover:bg-koreyaah-red"><FacebookIcon size={20} /></a>
            </div>
          </Reveal>
        </div>

        {/* Form */}
        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="contact-form" className="rounded-[2.5rem] bg-koreyaah-cobalt p-6 md:p-10 text-koreyaah-cream">
            <h2 className="font-display text-3xl font-semibold uppercase tracking-tight">Send a message</h2>
            <div className="mt-8 space-y-5">
              <div>
                <label className="mb-2 block text-sm font-semibold">Name <span className="text-koreyaah-red">*</span></label>
                <input name="name" value={form.name} onChange={onChange} required data-testid="contact-input-name" placeholder="Your name" className="w-full rounded-2xl border-2 border-transparent bg-koreyaah-cream px-4 py-3 text-koreyaah-indigo outline-none transition-colors duration-200 focus:border-koreyaah-red" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold">Email <span className="text-koreyaah-red">*</span></label>
                <input type="email" name="email" value={form.email} onChange={onChange} required data-testid="contact-input-email" placeholder="you@email.com" className="w-full rounded-2xl border-2 border-transparent bg-koreyaah-cream px-4 py-3 text-koreyaah-indigo outline-none transition-colors duration-200 focus:border-koreyaah-red" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold">Phone</label>
                <input name="phone" value={form.phone} onChange={onChange} data-testid="contact-input-phone" placeholder="+91 ..." className="w-full rounded-2xl border-2 border-transparent bg-koreyaah-cream px-4 py-3 text-koreyaah-indigo outline-none transition-colors duration-200 focus:border-koreyaah-red" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold">Message <span className="text-koreyaah-red">*</span></label>
                <textarea name="message" value={form.message} onChange={onChange} required rows={5} data-testid="contact-input-message" placeholder="How can we help?" className="w-full rounded-2xl border-2 border-transparent bg-koreyaah-cream px-4 py-3 text-koreyaah-indigo outline-none transition-colors duration-200 focus:border-koreyaah-red" />
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="contact-submit" className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-koreyaah-red px-8 py-4 text-base font-bold uppercase tracking-wide text-white transition-transform duration-200 hover:-translate-y-1 hover:bg-koreyaah-red-deep disabled:opacity-60">
              {loading ? "Sending..." : <>Send Message <ArrowRight size={18} /></>}
            </button>
          </form>
        </Reveal>
      </section>

      {/* Our Locations */}
      <section className="bg-koreyaah-ghost py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal><p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">Our Locations</p></Reveal>
          <Reveal delay={0.05}><h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold uppercase tracking-tight text-koreyaah-indigo">Find your nearest K-Spot</h2></Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STORES.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.07}>
                <div data-testid={`contact-store-${i}`} className="flex h-full flex-col overflow-hidden rounded-[2rem] border-2 border-koreyaah-indigo/10 bg-white">
                  <iframe title={s.name} src={s.embed} className="h-56 w-full" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-display text-xl font-semibold text-koreyaah-indigo">{s.label}</h3>
                      <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${s.status === "open" ? "bg-koreyaah-red text-white" : "border border-koreyaah-indigo/30 text-koreyaah-indigo/70"}`}>
                        {s.status === "open" ? "Open" : "Coming Soon"}
                      </span>
                    </div>
                    <div className="mt-4 flex flex-1 flex-col text-sm text-koreyaah-indigo/75">
                      <p className="flex flex-1 gap-2"><MapPin size={16} className="mt-0.5 shrink-0 text-koreyaah-red" /><span>{s.address}</span></p>
                      <div className="mt-4 space-y-2.5 border-t border-koreyaah-indigo/10 pt-4">
                        <p className="flex items-center gap-2"><Clock size={16} className="shrink-0 text-koreyaah-red" /> Open daily · {CONTACT.hours}</p>
                        <a href={`tel:${CONTACT.phoneLink}`} className="flex items-center gap-2 transition-colors duration-200 hover:text-koreyaah-red"><Phone size={16} className="shrink-0 text-koreyaah-red" /> {CONTACT.phone}</a>
                      </div>
                    </div>
                    <a href={s.directions} target="_blank" rel="noreferrer" data-testid={`contact-store-directions-${i}`} className="group mt-5 inline-flex items-center gap-2 rounded-full bg-koreyaah-indigo px-6 py-3 text-sm font-bold uppercase tracking-wide text-white transition-transform duration-200 hover:-translate-y-1">
                      Get Directions <ArrowRight size={16} />
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
